import { inject, Injectable } from '@angular/core';
import { map, Observable, of, Subject } from 'rxjs';
import { HttpCacheService } from '@rcp/common-web';
import {
    AccountBillerResponse,
    AccountUserControllerService,
} from '@core/generated/openapi';
import { CACHE_KEYS } from '@core/constants/cache-keys.constants';
import { AuthService } from '@services/auth/auth.service';

/**
 * Cached for an hour and keyed on the request, which for a biller is only their
 * id - so a change to what that biller covers would keep serving the old scope.
 */
const BILLER_SCOPED_CACHE_TAGS = [
    CACHE_KEYS.AR_SNAPSHOT.findARSnapshotAgingTrendMetrics,
    CACHE_KEYS.AR_SNAPSHOT.findARSnapshotAgingTrendMetricSummary,
    CACHE_KEYS.CLAIM.getDaysToCompleteClaims,
];

@Injectable({
    providedIn: 'root',
})
export class AccountBillerService {
    private readonly service = inject(AccountUserControllerService);
    private readonly httpCacheService = inject(HttpCacheService);
    private readonly authService = inject(AuthService);

    private readonly billersChangedSubject = new Subject<void>();

    findAllActiveAccountBillers(
        accountId: number,
    ): Observable<Array<AccountBillerResponse>> {
        if (accountId == null) {
            return of([]);
        }

        return this.service.findActiveBillersByAccountId(accountId);
    }

    /**
     * The payers a biller owns on the active account, for narrowing payer
     * pickers to their scope. Null means every payer: no biller is selected,
     * or the biller owns all payers.
     */
    findPayerScope(billerId: number | undefined): Observable<number[] | null> {
        const accountId =
            this.authService.getCurrentUser()?.value?.activeAccount?.id;

        if (billerId == null || accountId == null) {
            return of(null);
        }

        return this.service.findResponsibilitiesForUser(billerId).pipe(
            map((responsibilities) => {
                const responsibility = (responsibilities ?? []).find(
                    (candidate) => candidate.account?.id === accountId,
                );

                if (!responsibility?.isBiller) {
                    return [];
                }

                return responsibility.allPayors
                    ? null
                    : Array.from(responsibility.responsiblePayors ?? [])
                          .map((payor) => payor.id)
                          .filter((id): id is number => id != null);
            }),
        );
    }

    /**
     * The header biller dropdown otherwise only reloads when the session emits,
     * so editing responsibility or a user's status in the Organization area
     * would not reach it until the next login or account switch.
     */
    billersChanged$(): Observable<void> {
        return this.billersChangedSubject.asObservable();
    }

    notifyBillersChanged(): void {
        BILLER_SCOPED_CACHE_TAGS.forEach((tag) =>
            this.httpCacheService.invalidateTag(tag),
        );
        this.billersChangedSubject.next();
    }
}
